import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Tab, Tabs } from '@mui/material';
import { Flex, Icon, Typo, theme } from '../../../ui';
import { CHANNEL_TABS } from '../data/classroomConstants';
import { MOCK_DISCUSSION_MESSAGES, MOCK_FILES, getMockSubmissions, getMockQuizAttempts } from '../data/mockClassroomData';
import { useClassroomEditing } from '../hooks';
import AddAssignmentModal from './AddAssignmentModal';
import AddQuizModal from './AddQuizModal';
import AddFileModal from './AddFileModal';
import AddDiscussionModal from './AddDiscussionModal';
import EditWebinarModal from './EditWebinarModal';

const ChannelContainer = styled.div`
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ChannelHeader = styled(Flex)`
  padding: 20px 24px 0;
  background: ${theme.colors.NEU_0};
  border-bottom: 1px solid ${theme.colors.NEU_100};
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const PageCard = styled.div`
  background: ${theme.colors.NEU_0};
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid ${theme.colors.NEU_100};
`;

const EmptyTab = styled(Flex)`
  padding: 3rem;
  text-align: center;
`;

const StyledTabs = styled(Tabs)`
  .MuiTab-root {
    text-transform: none;
    font-weight: 500;
    font-size: 14px;
    min-height: 42px;
  }
`;

// ─── Live Lesson Styles ─────────────────────────────────────────────────────

const SectionLabel = styled(Flex)`
  margin-bottom: 12px;
  margin-top: ${({ $first }) => ($first ? '0' : '24px')};
`;

const LiveBadge = styled.span`
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
`;

const RecordedCard = styled.div`
  background: ${theme.colors.NEU_0};
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 10px;
  border: 1px solid ${theme.colors.NEU_100};
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: ${theme.colors.PRIMARY};
    background: ${theme.colors.PRIMARY_EXTRA_LIGHT};
  }
`;

const JoinButton = styled.button`
  padding: 10px 20px;
  background: ${theme.colors.PRIMARY};
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: opacity 0.15s;
  &:hover { opacity: 0.9; }
`;

const SecondaryButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  color: ${theme.colors.PRIMARY};
  border: 1px solid ${theme.colors.PRIMARY};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.15s;
  &:hover { background: ${theme.colors.PRIMARY_EXTRA_LIGHT}; }
`;

// ─── Discussion Styles ──────────────────────────────────────────────────────

const MessageBubble = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${theme.colors.NEU_50};
  &:last-child { border-bottom: none; }
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $isTeacher }) => ($isTeacher ? theme.colors.PRIMARY : theme.colors.NEU_100)};
  color: ${({ $isTeacher }) => ($isTeacher ? '#fff' : theme.colors.NEU_600)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
`;

const MessageInput = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: ${theme.colors.NEU_50};
  border-radius: 8px;
  margin-top: 12px;
  align-items: center;
`;

const FakeInput = styled.div`
  flex: 1;
  padding: 8px 12px;
  background: ${theme.colors.NEU_0};
  border: 1px solid ${theme.colors.NEU_100};
  border-radius: 6px;
  color: ${theme.colors.NEU_300};
  font-size: 14px;
`;

// ─── Files Styles ───────────────────────────────────────────────────────────

const FileRow = styled(Flex)`
  padding: 12px 16px;
  border-radius: 8px;
  transition: background 0.15s;
  &:hover { background: ${theme.colors.NEU_50}; }
`;

const FileIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ $type }) => {
    if ($type === 'pdf') return '#ffebee';
    if ($type === 'video') return '#e3f2fd';
    if ($type === 'doc') return '#e8f5e9';
    if ($type === 'ppt') return '#fff3e0';
    return theme.colors.NEU_50;
  }};
  color: ${({ $type }) => {
    if ($type === 'pdf') return '#c62828';
    if ($type === 'video') return '#1565c0';
    if ($type === 'doc') return '#2e7d32';
    if ($type === 'ppt') return '#e65100';
    return theme.colors.NEU_600;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  flex-shrink: 0;
`;

// ─── Shared Styles ──────────────────────────────────────────────────────────

const ProgressBar = styled.div`
  height: 6px;
  background: ${theme.colors.NEU_100};
  border-radius: 3px;
  overflow: hidden;
  flex: 1;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${({ $complete }) => ($complete ? '#2e7d32' : theme.colors.PRIMARY)};
  border-radius: 3px;
  width: ${({ $progress }) => `${$progress}%`};
  transition: width 0.3s ease;
`;

const DueBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 4px;
  background: ${({ $urgent }) => ($urgent ? '#fff3e0' : theme.colors.NEU_50)};
  color: ${({ $urgent }) => ($urgent ? '#e65100' : theme.colors.NEU_600)};
`;

const TimeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  background: #ede7f6;
  color: #7b1fa2;
`;

const TabBadge = styled.span`
  background: ${theme.colors.PRIMARY};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  margin-left: 6px;
`;

// ─── Edit button styles ─────────────────────────────────────────────────────

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: transparent;
  color: ${theme.colors.PRIMARY};
  border: 1px solid ${theme.colors.PRIMARY};
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.15s;
  &:hover { background: ${theme.colors.PRIMARY_EXTRA_LIGHT}; }
`;

const EditIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: 1px solid ${theme.colors.NEU_200};
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover {
    border-color: ${theme.colors.PRIMARY};
    background: ${theme.colors.PRIMARY_EXTRA_LIGHT};
  }
`;

const ActionBar = styled(Flex)`
  margin-bottom: 16px;
`;

// ─── Teacher View: Submission / Results Styles ──────────────────────────────

const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 4px;
  background: ${({ $status }) => {
    if ($status === 'graded') return '#e8f5e9';
    if ($status === 'submitted') return '#e3f2fd';
    if ($status === 'late') return '#fff3e0';
    if ($status === 'pending') return theme.colors.NEU_50;
    if ($status === 'completed') return '#e8f5e9';
    if ($status === 'in_progress') return '#e3f2fd';
    if ($status === 'not_started') return theme.colors.NEU_50;
    return theme.colors.NEU_50;
  }};
  color: ${({ $status }) => {
    if ($status === 'graded') return '#2e7d32';
    if ($status === 'submitted') return '#1565c0';
    if ($status === 'late') return '#e65100';
    if ($status === 'pending') return theme.colors.NEU_600;
    if ($status === 'completed') return '#2e7d32';
    if ($status === 'in_progress') return '#1565c0';
    if ($status === 'not_started') return theme.colors.NEU_600;
    return theme.colors.NEU_600;
  }};
`;

const StudentRow = styled(Flex)`
  padding: 10px 0;
  border-bottom: 1px solid ${theme.colors.NEU_50};
  &:last-child { border-bottom: none; }
`;

const StudentAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${theme.colors.NEU_100};
  color: ${theme.colors.NEU_600};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;

const GradeBadge = styled.span`
  font-size: 13px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 4px;
  background: ${({ $grade }) => {
    if ($grade === 'A' || $grade === 'B') return '#e8f5e9';
    if ($grade === 'C' || $grade === 'D') return '#fff3e0';
    return '#ffebee';
  }};
  color: ${({ $grade }) => {
    if ($grade === 'A' || $grade === 'B') return '#2e7d32';
    if ($grade === 'C' || $grade === 'D') return '#e65100';
    return '#c62828';
  }};
`;

const ScoreBadge = styled.span`
  font-size: 13px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 4px;
  background: ${({ $score }) => {
    if ($score >= 80) return '#e8f5e9';
    if ($score >= 60) return '#fff3e0';
    return '#ffebee';
  }};
  color: ${({ $score }) => {
    if ($score >= 80) return '#2e7d32';
    if ($score >= 60) return '#e65100';
    return '#c62828';
  }};
`;

const StatBox = styled(Flex)`
  padding: 12px 16px;
  background: ${({ $bg }) => $bg || theme.colors.NEU_50};
  border-radius: 8px;
  min-width: 100px;
`;

const GradeButton = styled.button`
  padding: 4px 12px;
  background: ${theme.colors.PRIMARY};
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: opacity 0.15s;
  &:hover { opacity: 0.9; }
`;

const ModerationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s, background 0.15s;
  ${MessageBubble}:hover & { opacity: 1; }
  &:hover { background: ${theme.colors.NEU_100}; }
`;

const PinnedBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: #e65100;
  background: #fff3e0;
  padding: 1px 5px;
  border-radius: 3px;
  text-transform: uppercase;
`;

// ─── Component ──────────────────────────────────────────────────────────────

const ChannelView = ({ section, courseId, groupId, isDemo, members }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(CHANNEL_TABS.LIVE);
  const [activeModal, setActiveModal] = useState(null);
  const [editingWebinar, setEditingWebinar] = useState(null);
  const [expandedAssignment, setExpandedAssignment] = useState(null);
  const [expandedQuiz, setExpandedQuiz] = useState(null);
  const [pinnedMessages, setPinnedMessages] = useState({});

  const pages = section?.pages ?? [];
  const sectionId = section?.id;

  // Get students from members (exclude teachers)
  const students = useMemo(
    () => (members ?? []).filter((m) => m.role === 'reader'),
    [members]
  );

  const {
    localPages,
    localFiles,
    addAssignment,
    addFile,
    addDiscussion,
    editWebinarName,
    addQuiz,
  } = useClassroomEditing({ courseId, sectionId, isDemo });

  // Merge original pages with locally-added pages
  const allPages = useMemo(() => [...pages, ...localPages], [pages, localPages]);

  // Categorize pages by their type or name
  const categorizedPages = useMemo(() => {
    const result = { live: [], discussion: [], files: [], assignments: [], assessment: [] };

    allPages.forEach((page) => {
      const nameLower = (page.name || '').toLowerCase();
      const type = page.type || '';

      if (type === 'webinar' || nameLower.includes('live') || nameLower.includes('webinar') || nameLower.includes('lesson')) {
        result.live.push(page);
      } else if (type === 'discussion' || nameLower.includes('discussion') || nameLower.includes('chat') || nameLower.includes('file')) {
        result.discussion.push(page);
      } else if (type === 'quiz' || nameLower.includes('quiz') || nameLower.includes('exam') || nameLower.includes('assessment')) {
        result.assessment.push(page);
      } else if (type === 'task' || nameLower.includes('assignment') || nameLower.includes('task')) {
        result.assignments.push(page);
      } else {
        result.assignments.push(page);
      }
    });

    return result;
  }, [allPages]);

  // Get discussion messages and files for this section, merged with local additions
  const discussionMessages = MOCK_DISCUSSION_MESSAGES[`${sectionId}-discussion`] ?? [];
  const sectionFiles = useMemo(
    () => [...(MOCK_FILES[sectionId] ?? []), ...localFiles],
    [sectionId, localFiles]
  );

  const formatScheduledDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date - now;
    const diffDays = Math.floor(diffMs / 86400000);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (diffDays === 0) return `Today at ${time}`;
    if (diffDays === 1) return `Tomorrow at ${time}`;
    if (diffDays < 7) return `${dayName} at ${time}`;
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) + ` at ${time}`;
  };

  // ─── Live Lessons Tab (unchanged) ───────────────────────────────────────────

  const renderLiveTab = () => {
    const upcoming = categorizedPages.live.filter((p) => p.status === 'upcoming');
    const recorded = categorizedPages.live.filter((p) => p.status === 'recorded')
      .sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate));

    return (
      <>
        {categorizedPages.live.length === 0 && (
          <EmptyTab direction='column' gap='8' align='center'>
            <Icon.Webinar color={theme.colors.NEU_300} size='large' />
            <Typo.MediumText color={theme.colors.NEU_300}>
              {t('classroom.noLiveLessons', 'No live lessons scheduled')}
            </Typo.MediumText>
          </EmptyTab>
        )}

        {upcoming.map((page) => (
          <PageCard key={page.id}>
            <Flex align='center' justify='space-between' style={{ marginBottom: 12 }}>
              <Flex align='center' gap='10'>
                <Icon.Webinar color={theme.colors.PRIMARY} />
                <Typo.ContentSmallHeader>
                  {t('classroom.nextSession', 'Next Session')}
                </Typo.ContentSmallHeader>
                <LiveBadge>{t('classroom.upcoming', 'Upcoming')}</LiveBadge>
              </Flex>
              <EditIconButton onClick={() => setEditingWebinar(page)} title={t('classroom.editName', 'Edit name')}>
                <Icon.Edit size='x-small' color={theme.colors.NEU_300} />
              </EditIconButton>
            </Flex>

            {page.topic && (
              <Typo.MediumText style={{ fontWeight: 500, marginBottom: 10 }}>
                {page.topic}
              </Typo.MediumText>
            )}

            {page.scheduledDate && (
              <Flex gap='8' align='center' style={{ marginBottom: 8 }}>
                <Icon.Time color={theme.colors.NEU_300} size='x-small' />
                <Typo.MediumText color={theme.colors.NEU_600}>
                  {formatScheduledDate(page.scheduledDate)}
                </Typo.MediumText>
              </Flex>
            )}

            {page.teacherName && (
              <Flex gap='8' align='center' style={{ marginBottom: 16 }}>
                <Icon.Profile color={theme.colors.NEU_300} size='x-small' />
                <Typo.MediumText color={theme.colors.NEU_600}>
                  {page.teacherName}
                </Typo.MediumText>
              </Flex>
            )}

            <JoinButton>{t('classroom.joinSession', 'Join Live Session')}</JoinButton>
          </PageCard>
        ))}

        {recorded.length > 0 && (
          <>
            <SectionLabel align='center' gap='8' $first={upcoming.length === 0}>
              <Typo.ContentSmallHeader>
                {t('classroom.pastRecordings', 'Past Recordings')}
              </Typo.ContentSmallHeader>
              <Typo.SmallText color={theme.colors.NEU_300}>
                ({recorded.length})
              </Typo.SmallText>
            </SectionLabel>

            {recorded.map((page) => {
              const progressPct = page.progress != null ? Math.round(page.progress * 100) : null;
              const dateStr = page.scheduledDate
                ? new Date(page.scheduledDate).toLocaleDateString('en-US', {
                    weekday: 'short', month: 'short', day: 'numeric',
                  })
                : '';

              return (
                <RecordedCard key={page.id}>
                  <Flex align='center' justify='space-between'>
                    <Flex align='center' gap='12' style={{ flex: 1, minWidth: 0 }}>
                      <Icon.Webinar color={theme.colors.NEU_300} size='small' />
                      <Flex direction='column' gap='2' style={{ flex: 1, minWidth: 0 }}>
                        <Flex align='center' gap='8'>
                          <Typo.MediumText style={{ fontWeight: 500 }}>
                            {page.topic || page.name}
                          </Typo.MediumText>
                          {progressPct === 100 && (
                            <span style={{ fontSize: 11, color: '#2e7d32', fontWeight: 600 }}>
                              ✓
                            </span>
                          )}
                        </Flex>
                        <Flex gap='12' align='center'>
                          <Typo.SmallText color={theme.colors.NEU_300}>{dateStr}</Typo.SmallText>
                          {page.recordingDuration && (
                            <Typo.SmallText color={theme.colors.NEU_300}>{page.recordingDuration}</Typo.SmallText>
                          )}
                          {page.viewCount && (
                            <Typo.SmallText color={theme.colors.NEU_300}>
                              {page.viewCount} views
                            </Typo.SmallText>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>

                    <Flex align='center' gap='12'>
                      {progressPct != null && progressPct < 100 && (
                        <Flex gap='6' align='center' style={{ width: 100 }}>
                          <ProgressBar>
                            <ProgressFill $progress={progressPct} $complete={false} />
                          </ProgressBar>
                          <Typo.SmallText color={theme.colors.NEU_300} style={{ whiteSpace: 'nowrap' }}>
                            {progressPct}%
                          </Typo.SmallText>
                        </Flex>
                      )}
                      <EditIconButton onClick={(e) => { e.stopPropagation(); setEditingWebinar(page); }} title={t('classroom.editName', 'Edit name')}>
                        <Icon.Edit size='x-small' color={theme.colors.NEU_300} />
                      </EditIconButton>
                      <SecondaryButton style={{ padding: '6px 14px', fontSize: 13 }}>
                        {progressPct != null && progressPct > 0 && progressPct < 100
                          ? t('classroom.continueWatching', 'Continue')
                          : t('classroom.watchRecording', 'Watch')}
                      </SecondaryButton>
                    </Flex>
                  </Flex>
                </RecordedCard>
              );
            })}
          </>
        )}
      </>
    );
  };

  // ─── Discussion Tab (Teacher View: moderation controls) ─────────────────────

  const renderDiscussionTab = () => {
    const togglePin = (msgId) => {
      setPinnedMessages((prev) => ({ ...prev, [msgId]: !prev[msgId] }));
    };

    // Sort: pinned messages first
    const sortedMessages = [...discussionMessages].sort((a, b) => {
      const aPinned = pinnedMessages[a.id] ? 1 : 0;
      const bPinned = pinnedMessages[b.id] ? 1 : 0;
      return bPinned - aPinned;
    });

    return (
      <>
        <ActionBar justify='flex-end' gap='8'>
          <AddButton onClick={() => setActiveModal('discussion')}>
            <Icon.Add size='x-small' color={theme.colors.PRIMARY} />
            {t('classroom.addDiscussion', 'Add Discussion')}
          </AddButton>
        </ActionBar>

        {discussionMessages.length === 0 ? (
          <EmptyTab direction='column' gap='8' align='center'>
            <Icon.Forum color={theme.colors.NEU_300} size='large' />
            <Typo.MediumText color={theme.colors.NEU_300}>
              {t('classroom.noDiscussions', 'No discussions yet. Start a conversation!')}
            </Typo.MediumText>
          </EmptyTab>
        ) : (
          <PageCard>
            <Flex align='center' gap='8' style={{ marginBottom: 16 }}>
              <Icon.Forum color={theme.colors.PRIMARY} />
              <Typo.ContentSmallHeader>
                {section?.name} — {t('classroom.discussion', 'Discussion')}
              </Typo.ContentSmallHeader>
              <TabBadge>{discussionMessages.length}</TabBadge>
            </Flex>

            {sortedMessages.map((msg) => (
              <MessageBubble key={msg.id}>
                <Avatar $isTeacher={msg.isTeacher}>{msg.avatar}</Avatar>
                <Flex direction='column' gap='4' style={{ flex: 1 }}>
                  <Flex gap='8' align='center'>
                    <Typo.SmallText style={{ fontWeight: 600 }}>
                      {msg.author}
                    </Typo.SmallText>
                    {msg.isTeacher && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: theme.colors.PRIMARY,
                        background: theme.colors.PRIMARY_EXTRA_LIGHT, padding: '1px 6px', borderRadius: 3,
                      }}>
                        Teacher
                      </span>
                    )}
                    {pinnedMessages[msg.id] && <PinnedBadge>Pinned</PinnedBadge>}
                    <Typo.SmallText color={theme.colors.NEU_300}>{msg.time}</Typo.SmallText>
                    <Flex gap='2' style={{ marginLeft: 'auto' }}>
                      <ModerationButton
                        onClick={() => togglePin(msg.id)}
                        title={pinnedMessages[msg.id] ? t('classroom.unpin', 'Unpin') : t('classroom.pin', 'Pin')}
                      >
                        <Icon.Label size='x-small' color={pinnedMessages[msg.id] ? '#e65100' : theme.colors.NEU_300} />
                      </ModerationButton>
                      <ModerationButton title={t('classroom.delete', 'Delete')}>
                        <Icon.Delete size='x-small' color={theme.colors.NEU_300} />
                      </ModerationButton>
                    </Flex>
                  </Flex>
                  <Typo.MediumText>{msg.text}</Typo.MediumText>
                </Flex>
              </MessageBubble>
            ))}

            <MessageInput>
              <FakeInput>{t('classroom.typeAnnouncement', 'Write an announcement or reply...')}</FakeInput>
              <Icon.Send color={theme.colors.NEU_300} size='small' />
            </MessageInput>
          </PageCard>
        )}
      </>
    );
  };

  // ─── Files Tab (Teacher View: management controls) ──────────────────────────

  const renderFilesTab = () => {
    return (
      <>
        <ActionBar justify='flex-end' gap='8'>
          <AddButton onClick={() => setActiveModal('file')}>
            <Icon.Add size='x-small' color={theme.colors.PRIMARY} />
            {t('classroom.addFile', 'Add File')}
          </AddButton>
        </ActionBar>

        {sectionFiles.length === 0 ? (
          <EmptyTab direction='column' gap='8' align='center'>
            <Icon.Folder color={theme.colors.NEU_300} size='large' />
            <Typo.MediumText color={theme.colors.NEU_300}>
              {t('classroom.filesDescription', 'Shared files, recordings, and documents will appear here.')}
            </Typo.MediumText>
          </EmptyTab>
        ) : (
          <PageCard>
            <Flex align='center' justify='space-between' style={{ marginBottom: 16 }}>
              <Flex align='center' gap='8'>
                <Icon.Folder color={theme.colors.PRIMARY} />
                <Typo.ContentSmallHeader>
                  {t('classroom.sharedFiles', 'Shared Files')}
                </Typo.ContentSmallHeader>
                <TabBadge>{sectionFiles.length}</TabBadge>
              </Flex>
            </Flex>

            {sectionFiles.map((file) => {
              const isStudentUpload = file.uploadedBy && !['Anna Lindberg', 'Erik Johansson', 'Maria Svensson', 'Lars Nilsson'].includes(file.uploadedBy);
              return (
                <FileRow key={file.id} align='center' gap='12'>
                  <FileIcon $type={file.type}>
                    {file.type}
                  </FileIcon>
                  <Flex direction='column' gap='2' style={{ flex: 1 }}>
                    <Flex align='center' gap='6'>
                      <Typo.MediumText style={{ fontWeight: 500 }}>{file.name}</Typo.MediumText>
                      {isStudentUpload && (
                        <span style={{
                          fontSize: 9, fontWeight: 600, color: '#1565c0',
                          background: '#e3f2fd', padding: '1px 5px', borderRadius: 3,
                        }}>
                          Student
                        </span>
                      )}
                    </Flex>
                    <Typo.SmallText color={theme.colors.NEU_300}>
                      {file.size} · {file.uploadedBy} · {file.uploadedAt}
                    </Typo.SmallText>
                  </Flex>
                  <Flex gap='6'>
                    <EditIconButton title={t('classroom.download', 'Download')}>
                      <Icon.Download size='x-small' color={theme.colors.NEU_300} />
                    </EditIconButton>
                    <EditIconButton title={t('classroom.deleteFile', 'Delete file')}>
                      <Icon.Delete size='x-small' color={theme.colors.NEU_300} />
                    </EditIconButton>
                  </Flex>
                </FileRow>
              );
            })}
          </PageCard>
        )}
      </>
    );
  };

  // ─── Assignments Tab (Teacher View: submissions overview) ───────────────────

  const renderAssignmentsTab = () => {
    return (
      <>
        <ActionBar justify='flex-end' gap='8'>
          <AddButton onClick={() => setActiveModal('assignment')}>
            <Icon.Add size='x-small' color={theme.colors.PRIMARY} />
            {t('classroom.addAssignment', 'Add Assignment')}
          </AddButton>
        </ActionBar>

        {categorizedPages.assignments.length === 0 && (
          <EmptyTab direction='column' gap='8' align='center'>
            <Icon.Task color={theme.colors.NEU_300} size='large' />
            <Typo.MediumText color={theme.colors.NEU_300}>
              {t('classroom.noAssignments', 'No assignments yet')}
            </Typo.MediumText>
          </EmptyTab>
        )}

        {categorizedPages.assignments.map((page) => {
          const submissions = getMockSubmissions(page.id, students);
          const graded = submissions.filter((s) => s.status === 'graded');
          const submitted = submissions.filter((s) => s.status === 'submitted');
          const late = submissions.filter((s) => s.status === 'late');
          const pending = submissions.filter((s) => s.status === 'pending');
          const needsReview = submitted.length + late.length;
          const submittedPct = students.length > 0
            ? Math.round(((graded.length + submitted.length + late.length) / students.length) * 100)
            : 0;
          const isExpanded = expandedAssignment === page.id;

          return (
            <PageCard key={page.id}>
              <Flex align='center' justify='space-between' style={{ marginBottom: 8 }}>
                <Flex align='center' gap='10'>
                  <Icon.Task color={page.isLocked ? theme.colors.NEU_300 : theme.colors.PRIMARY} size='small' />
                  <Typo.ContentSmallHeader style={{ opacity: page.isLocked ? 0.5 : 1 }}>
                    {page.name || t('classroom.assignment', 'Assignment')}
                  </Typo.ContentSmallHeader>
                  {page.isLocked && (
                    <Flex gap='4' align='center'>
                      <Icon.Lock color={theme.colors.NEU_300} size='x-small' />
                      <Typo.SmallText color={theme.colors.NEU_300}>
                        {t('classroom.locked', 'Locked')}
                      </Typo.SmallText>
                    </Flex>
                  )}
                </Flex>
                {page.dueDate && !page.isLocked && (
                  <DueBadge $urgent={new Date(page.dueDate) - new Date() < 3 * 86400000}>
                    Due {new Date(page.dueDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </DueBadge>
                )}
              </Flex>

              {page.description && !page.isLocked && (
                <Typo.MediumText color={theme.colors.NEU_600} style={{ marginBottom: 12 }}>
                  {page.description}
                </Typo.MediumText>
              )}

              {!page.isLocked && (
                <>
                  {/* Submission stats */}
                  <Flex gap='12' style={{ marginBottom: 12 }}>
                    <StatBox direction='column' gap='2' $bg='#e8f5e9'>
                      <Typo.SmallText color='#2e7d32' style={{ fontWeight: 600 }}>{graded.length}</Typo.SmallText>
                      <Typo.SmallText color='#2e7d32'>{t('classroom.graded', 'Graded')}</Typo.SmallText>
                    </StatBox>
                    <StatBox direction='column' gap='2' $bg={needsReview > 0 ? '#e3f2fd' : theme.colors.NEU_50}>
                      <Typo.SmallText color={needsReview > 0 ? '#1565c0' : theme.colors.NEU_600} style={{ fontWeight: 600 }}>
                        {needsReview}
                      </Typo.SmallText>
                      <Typo.SmallText color={needsReview > 0 ? '#1565c0' : theme.colors.NEU_600}>
                        {t('classroom.needsReview', 'To Review')}
                      </Typo.SmallText>
                    </StatBox>
                    <StatBox direction='column' gap='2'>
                      <Typo.SmallText color={theme.colors.NEU_600} style={{ fontWeight: 600 }}>{pending.length}</Typo.SmallText>
                      <Typo.SmallText color={theme.colors.NEU_600}>{t('classroom.notSubmitted', 'Not Submitted')}</Typo.SmallText>
                    </StatBox>
                  </Flex>

                  {/* Progress bar: % submitted */}
                  <Flex gap='8' align='center' style={{ marginBottom: 12 }}>
                    <ProgressBar>
                      <ProgressFill $progress={submittedPct} $complete={submittedPct === 100} />
                    </ProgressBar>
                    <Typo.SmallText color={theme.colors.NEU_600} style={{ whiteSpace: 'nowrap' }}>
                      {submittedPct}% {t('classroom.submitted', 'submitted')}
                    </Typo.SmallText>
                  </Flex>

                  {/* Expand/collapse student list */}
                  <SecondaryButton
                    style={{ padding: '6px 14px', fontSize: 13 }}
                    onClick={() => setExpandedAssignment(isExpanded ? null : page.id)}
                  >
                    {isExpanded
                      ? t('classroom.hideStudents', 'Hide Student List')
                      : t('classroom.viewSubmissions', 'View Submissions')}
                  </SecondaryButton>

                  {isExpanded && (
                    <div style={{ marginTop: 16 }}>
                      {submissions.map((sub) => (
                        <StudentRow key={sub.studentId} align='center' gap='12'>
                          <StudentAvatar>{sub.studentName.charAt(0)}</StudentAvatar>
                          <Flex direction='column' gap='2' style={{ flex: 1, minWidth: 0 }}>
                            <Typo.MediumText style={{ fontWeight: 500 }}>{sub.studentName}</Typo.MediumText>
                            {sub.submittedAt && (
                              <Typo.SmallText color={theme.colors.NEU_300}>
                                {sub.status === 'late' ? `Submitted ${sub.submittedAt}` : `Submitted ${sub.submittedAt}`}
                              </Typo.SmallText>
                            )}
                            {sub.feedback && (
                              <Typo.SmallText color={theme.colors.NEU_600} style={{ fontStyle: 'italic', marginTop: 2 }}>
                                &ldquo;{sub.feedback}&rdquo;
                              </Typo.SmallText>
                            )}
                          </Flex>
                          <Flex align='center' gap='8'>
                            <StatusBadge $status={sub.status}>
                              {sub.status === 'graded' ? t('classroom.graded', 'Graded')
                                : sub.status === 'submitted' ? t('classroom.submittedStatus', 'Submitted')
                                : sub.status === 'late' ? t('classroom.late', 'Late')
                                : t('classroom.pending', 'Pending')}
                            </StatusBadge>
                            {sub.grade && <GradeBadge $grade={sub.grade}>{sub.grade}</GradeBadge>}
                            {(sub.status === 'submitted' || sub.status === 'late') && (
                              <GradeButton>{t('classroom.grade', 'Grade')}</GradeButton>
                            )}
                          </Flex>
                        </StudentRow>
                      ))}
                    </div>
                  )}
                </>
              )}

              {page.isLocked && (
                <Typo.SmallText color={theme.colors.NEU_300} style={{ marginTop: 8 }}>
                  {t('classroom.lockedDescription', 'Complete previous assignments to unlock this content.')}
                </Typo.SmallText>
              )}
            </PageCard>
          );
        })}
      </>
    );
  };

  // ─── Assessment Tab (Teacher View: results grid) ────────────────────────────

  const renderAssessmentTab = () => {
    return (
      <>
        <ActionBar justify='flex-end' gap='8'>
          <AddButton onClick={() => setActiveModal('quiz')}>
            <Icon.Add size='x-small' color={theme.colors.PRIMARY} />
            {t('classroom.addQuiz', 'Add Quiz / Assessment')}
          </AddButton>
        </ActionBar>

        {categorizedPages.assessment.length === 0 && (
          <EmptyTab direction='column' gap='8' align='center'>
            <Icon.Quiz color={theme.colors.NEU_300} size='large' />
            <Typo.MediumText color={theme.colors.NEU_300}>
              {t('classroom.noAssessments', 'No assessments yet')}
            </Typo.MediumText>
          </EmptyTab>
        )}

        {categorizedPages.assessment.map((page) => {
          const parseTimeLimit = (tl) => {
            if (!tl) return null;
            const match = String(tl).match(/(\d+)/);
            return match ? parseInt(match[1], 10) : null;
          };
          const timeLimitMin = parseTimeLimit(page.timeLimit);
          const attempts = getMockQuizAttempts(page.id, students, page.questionCount || 10, timeLimitMin || 30);

          const completed = attempts.filter((a) => a.status === 'completed');
          const inProgress = attempts.filter((a) => a.status === 'in_progress');
          const notStarted = attempts.filter((a) => a.status === 'not_started');
          const avgScore = completed.length > 0
            ? Math.round(completed.reduce((sum, a) => sum + a.score, 0) / completed.length)
            : null;
          const completionPct = students.length > 0
            ? Math.round((completed.length / students.length) * 100)
            : 0;
          const isExpanded = expandedQuiz === page.id;

          return (
            <PageCard key={page.id}>
              <Flex align='center' justify='space-between' style={{ marginBottom: 8 }}>
                <Flex align='center' gap='10'>
                  <Icon.Quiz color={page.isLocked ? theme.colors.NEU_300 : '#7b1fa2'} size='small' />
                  <Typo.ContentSmallHeader style={{ opacity: page.isLocked ? 0.5 : 1 }}>
                    {page.name || t('classroom.assessment', 'Assessment')}
                  </Typo.ContentSmallHeader>
                  {page.timeLimit && !page.isLocked && (
                    <TimeBadge>
                      <Icon.Time size='x-small' color='#7b1fa2' />
                      {page.timeLimit}
                    </TimeBadge>
                  )}
                  {page.isLocked && (
                    <Flex gap='4' align='center'>
                      <Icon.Lock color={theme.colors.NEU_300} size='x-small' />
                      <Typo.SmallText color={theme.colors.NEU_300}>
                        {t('classroom.locked', 'Locked')}
                      </Typo.SmallText>
                    </Flex>
                  )}
                </Flex>
                {page.questionCount && !page.isLocked && (
                  <Typo.SmallText color={theme.colors.NEU_600}>
                    {page.questionCount} {t('classroom.questions', 'questions')}
                  </Typo.SmallText>
                )}
              </Flex>

              {!page.isLocked && (
                <>
                  {/* Summary stats */}
                  <Flex gap='12' style={{ marginBottom: 12 }}>
                    <StatBox direction='column' gap='2' $bg='#e8f5e9'>
                      <Typo.SmallText color='#2e7d32' style={{ fontWeight: 600 }}>{completed.length}</Typo.SmallText>
                      <Typo.SmallText color='#2e7d32'>{t('classroom.completed', 'Completed')}</Typo.SmallText>
                    </StatBox>
                    <StatBox direction='column' gap='2' $bg='#e3f2fd'>
                      <Typo.SmallText color='#1565c0' style={{ fontWeight: 600 }}>{inProgress.length}</Typo.SmallText>
                      <Typo.SmallText color='#1565c0'>{t('classroom.inProgress', 'In Progress')}</Typo.SmallText>
                    </StatBox>
                    <StatBox direction='column' gap='2'>
                      <Typo.SmallText color={theme.colors.NEU_600} style={{ fontWeight: 600 }}>{notStarted.length}</Typo.SmallText>
                      <Typo.SmallText color={theme.colors.NEU_600}>{t('classroom.notStarted', 'Not Started')}</Typo.SmallText>
                    </StatBox>
                    {avgScore != null && (
                      <StatBox direction='column' gap='2' $bg='#ede7f6'>
                        <Typo.SmallText color='#7b1fa2' style={{ fontWeight: 600 }}>{avgScore}%</Typo.SmallText>
                        <Typo.SmallText color='#7b1fa2'>{t('classroom.avgScore', 'Avg Score')}</Typo.SmallText>
                      </StatBox>
                    )}
                  </Flex>

                  {/* Completion progress */}
                  <Flex gap='8' align='center' style={{ marginBottom: 12 }}>
                    <ProgressBar>
                      <ProgressFill $progress={completionPct} $complete={completionPct === 100} />
                    </ProgressBar>
                    <Typo.SmallText color={theme.colors.NEU_600} style={{ whiteSpace: 'nowrap' }}>
                      {completionPct}% {t('classroom.completionRate', 'completed')}
                    </Typo.SmallText>
                  </Flex>

                  {/* Expand/collapse results */}
                  <SecondaryButton
                    style={{ padding: '6px 14px', fontSize: 13 }}
                    onClick={() => setExpandedQuiz(isExpanded ? null : page.id)}
                  >
                    {isExpanded
                      ? t('classroom.hideResults', 'Hide Results')
                      : t('classroom.viewResults', 'View Results')}
                  </SecondaryButton>

                  {isExpanded && (
                    <div style={{ marginTop: 16 }}>
                      {/* Table header */}
                      <Flex align='center' gap='12' style={{ padding: '8px 0', borderBottom: `2px solid ${theme.colors.NEU_100}` }}>
                        <Typo.SmallText style={{ fontWeight: 600, flex: 1, minWidth: 160 }}>
                          {t('classroom.student', 'Student')}
                        </Typo.SmallText>
                        <Typo.SmallText style={{ fontWeight: 600, width: 80, textAlign: 'center' }}>
                          {t('classroom.status', 'Status')}
                        </Typo.SmallText>
                        <Typo.SmallText style={{ fontWeight: 600, width: 60, textAlign: 'center' }}>
                          {t('classroom.score', 'Score')}
                        </Typo.SmallText>
                        <Typo.SmallText style={{ fontWeight: 600, width: 80, textAlign: 'center' }}>
                          {t('classroom.correct', 'Correct')}
                        </Typo.SmallText>
                        <Typo.SmallText style={{ fontWeight: 600, width: 70, textAlign: 'center' }}>
                          {t('classroom.time', 'Time')}
                        </Typo.SmallText>
                      </Flex>

                      {attempts.map((attempt) => (
                        <StudentRow key={attempt.studentId} align='center' gap='12'>
                          <Flex align='center' gap='8' style={{ flex: 1, minWidth: 160 }}>
                            <StudentAvatar>{attempt.studentName.charAt(0)}</StudentAvatar>
                            <Typo.MediumText style={{ fontWeight: 500 }}>{attempt.studentName}</Typo.MediumText>
                          </Flex>
                          <div style={{ width: 80, textAlign: 'center' }}>
                            <StatusBadge $status={attempt.status}>
                              {attempt.status === 'completed' ? t('classroom.done', 'Done')
                                : attempt.status === 'in_progress' ? t('classroom.active', 'Active')
                                : t('classroom.notStartedShort', '—')}
                            </StatusBadge>
                          </div>
                          <div style={{ width: 60, textAlign: 'center' }}>
                            {attempt.score != null ? (
                              <ScoreBadge $score={attempt.score}>{attempt.score}%</ScoreBadge>
                            ) : (
                              <Typo.SmallText color={theme.colors.NEU_300}>—</Typo.SmallText>
                            )}
                          </div>
                          <div style={{ width: 80, textAlign: 'center' }}>
                            {attempt.correctAnswers != null ? (
                              <Typo.SmallText color={theme.colors.NEU_600}>
                                {attempt.correctAnswers}/{attempt.totalQuestions}
                              </Typo.SmallText>
                            ) : (
                              <Typo.SmallText color={theme.colors.NEU_300}>—</Typo.SmallText>
                            )}
                          </div>
                          <div style={{ width: 70, textAlign: 'center' }}>
                            {attempt.timeSpent ? (
                              <Typo.SmallText color={theme.colors.NEU_600}>{attempt.timeSpent}</Typo.SmallText>
                            ) : (
                              <Typo.SmallText color={theme.colors.NEU_300}>—</Typo.SmallText>
                            )}
                          </div>
                        </StudentRow>
                      ))}
                    </div>
                  )}
                </>
              )}

              {page.isLocked && (
                <Typo.SmallText color={theme.colors.NEU_300} style={{ marginTop: 8 }}>
                  {t('classroom.lockedDescription', 'Complete previous assignments to unlock this content.')}
                </Typo.SmallText>
              )}
            </PageCard>
          );
        })}
      </>
    );
  };

  // ─── Tab rendering ─────────────────────────────────────────────────────────

  const renderTabContent = () => {
    switch (activeTab) {
      case CHANNEL_TABS.LIVE: return renderLiveTab();
      case CHANNEL_TABS.DISCUSSION: return renderDiscussionTab();
      case CHANNEL_TABS.FILES: return renderFilesTab();
      case CHANNEL_TABS.ASSIGNMENTS: return renderAssignmentsTab();
      case CHANNEL_TABS.ASSESSMENT: return renderAssessmentTab();
      default: return null;
    }
  };

  return (
    <ChannelContainer>
      <ChannelHeader direction='column'>
        <Flex align='center' gap='8' style={{ marginBottom: 8 }}>
          <Typo.ContentSmallHeader>
            {section?.name || t('classroom.channel', 'Unit')}
          </Typo.ContentSmallHeader>
        </Flex>
        <StyledTabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          indicatorColor='primary'
        >
          <Tab
            value={CHANNEL_TABS.LIVE}
            label={t('classroom.tabLive', 'Live Lessons')}
            icon={<Icon.Webinar size='x-small' />}
            iconPosition='start'
          />
          <Tab
            value={CHANNEL_TABS.DISCUSSION}
            label={
              <Flex align='center' gap='4'>
                {t('classroom.tabDiscussion', 'Discussion')}
                {discussionMessages.length > 0 && <TabBadge>{discussionMessages.length}</TabBadge>}
              </Flex>
            }
            icon={<Icon.Forum size='x-small' />}
            iconPosition='start'
          />
          <Tab
            value={CHANNEL_TABS.FILES}
            label={
              <Flex align='center' gap='4'>
                {t('classroom.tabFiles', 'Files')}
                {sectionFiles.length > 0 && <TabBadge>{sectionFiles.length}</TabBadge>}
              </Flex>
            }
            icon={<Icon.Folder size='x-small' />}
            iconPosition='start'
          />
          <Tab
            value={CHANNEL_TABS.ASSIGNMENTS}
            label={t('classroom.tabAssignments', 'Assignments')}
            icon={<Icon.Task size='x-small' />}
            iconPosition='start'
          />
          <Tab
            value={CHANNEL_TABS.ASSESSMENT}
            label={
              <Flex align='center' gap='4'>
                {t('classroom.tabAssessment', 'Final Assessment')}
                {categorizedPages.assessment.length > 0 && <TabBadge>{categorizedPages.assessment.length}</TabBadge>}
              </Flex>
            }
            icon={<Icon.Quiz size='x-small' />}
            iconPosition='start'
          />
        </StyledTabs>
      </ChannelHeader>
      <TabContent>{renderTabContent()}</TabContent>

      {/* ─── Modals ─────────────────────────────────────────────────────── */}

      {activeModal === 'assignment' && (
        <AddAssignmentModal
          open
          onClose={() => setActiveModal(null)}
          onSubmit={addAssignment}
        />
      )}

      {activeModal === 'quiz' && (
        <AddQuizModal
          open
          onClose={() => setActiveModal(null)}
          onSubmit={addQuiz}
        />
      )}

      {activeModal === 'file' && (
        <AddFileModal
          open
          onClose={() => setActiveModal(null)}
          onSubmit={addFile}
        />
      )}

      {activeModal === 'discussion' && (
        <AddDiscussionModal
          open
          onClose={() => setActiveModal(null)}
          onSubmit={addDiscussion}
        />
      )}

      {editingWebinar && (
        <EditWebinarModal
          open
          onClose={() => setEditingWebinar(null)}
          onSubmit={async ({ name }) => {
            await editWebinarName({ pageId: editingWebinar.id, name });
            setEditingWebinar(null);
          }}
          currentName={editingWebinar.topic || editingWebinar.name}
        />
      )}
    </ChannelContainer>
  );
};

export default ChannelView;
